import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const STATUS_TONES = {
  approved: undefined, // default gold
  draft: "muted",
  under_review: "warn",
  rejected: "danger",
  retired: "muted",
};

const GUARDIAN_TONES = {
  pass: undefined,
  warn: "warn",
  block: "danger",
  pending: "muted",
};

function formatDollars(v) {
  if (v == null) return "$0.0000";
  return "$" + Number(v).toFixed(4);
}

export default function AssetLibrary() {
  const [assets, setAssets] = useState([]);
  const [versionsByAsset, setVersionsByAsset] = useState({});
  const [products, setProducts] = useState([]);
  const [productFilter, setProductFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assetTypeFilter, setAssetTypeFilter] = useState("");
  const [state, setState] = useState({ loading: true, error: null });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (productFilter) params.set("product_id", productFilter);
      if (statusFilter) params.set("status", statusFilter);
      if (assetTypeFilter) params.set("asset_type", assetTypeFilter);
      const [assetsRes, productsRes] = await Promise.all([
        api.get(`/assets?${params.toString()}`),
        api.get("/products"),
      ]);
      setAssets(assetsRes.data.assets || []);
      setProducts(productsRes.data.products || []);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, [productFilter, statusFilter, assetTypeFilter]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (assetId) => {
    if (versionsByAsset[assetId]) {
      setSelected(assetId === selected ? null : assetId);
      return;
    }
    try {
      const res = await api.get(`/assets/${assetId}`);
      setVersionsByAsset((prev) => ({ ...prev, [assetId]: res.data.versions || [] }));
      setSelected(assetId);
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    }
  }, [versionsByAsset, selected]);

  const productName = (id) => (products.find((p) => p.id === id) || {}).name || "—";

  return (
    <Shell title="Asset Library">
      <div className="bo-eyebrow">Create</div>
      <h1 className="bo-serif" style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 8px" }} data-testid="library-title">
        Every asset, with its lineage.
      </h1>
      <p style={{ color: "var(--bo-muted)", maxWidth: 720, marginBottom: 20 }} data-testid="library-lede">
        Every generated variant lives here — approved, rejected, or awaiting review. Guardian verdicts and edit history are preserved so you can trace how any approved piece of copy came to be.
      </p>

      <div className="bo-card" data-testid="library-filters" style={{ padding: 16, marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="bo-field" style={{ minWidth: 180 }}>
            <label className="bo-label">Product</label>
            <select className="bo-input" data-testid="library-product-filter" value={productFilter} onChange={(e) => setProductFilter(e.target.value)}>
              <option value="">All products</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="bo-field" style={{ minWidth: 160 }}>
            <label className="bo-label">Status</label>
            <select className="bo-input" data-testid="library-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="retired">Retired</option>
            </select>
          </div>
          <div className="bo-field" style={{ minWidth: 160 }}>
            <label className="bo-label">Asset type</label>
            <select className="bo-input" data-testid="library-type-filter" value={assetTypeFilter} onChange={(e) => setAssetTypeFilter(e.target.value)}>
              <option value="">All types</option>
              <option value="facebook_ad">Facebook ad</option>
              <option value="instagram_post">Instagram post</option>
              <option value="email_body">Email body</option>
              <option value="landing_headline">Landing headline</option>
              <option value="landing_hero">Landing hero</option>
              <option value="blog_outline">Blog outline</option>
              <option value="generic_copy">Generic copy</option>
            </select>
          </div>
        </div>
      </div>

      <ErrorBanner error={state.error} testId="library-error" />
      {state.loading && <LoadingCard label="Loading assets…" />}

      {!state.loading && assets.length === 0 && (
        <EmptyState title="No assets yet" description="Generate copy from the Creative Studio to see it here." />
      )}

      <div style={{ display: "grid", gap: 12 }}>
        {assets.map((a) => {
          const isOpen = selected === a.id;
          const versions = versionsByAsset[a.id] || [];
          return (
            <div key={a.id} className="bo-card" data-testid={`asset-card-${a.id}`} style={{ padding: 18 }}>
              <button
                onClick={() => loadDetail(a.id)}
                data-testid={`asset-toggle-${a.id}`}
                style={{
                  background: "transparent", border: "none", padding: 0, textAlign: "left",
                  width: "100%", cursor: "pointer", color: "inherit",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="bo-eyebrow">{a.asset_type} · {productName(a.product_id)}</div>
                    <h2 className="bo-card-title" style={{ margin: "4px 0 4px" }}>
                      {a.title || "(untitled)"}
                    </h2>
                    <div style={{ fontSize: 12, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}>
                      {a.id.slice(0, 8)} · v{a.latest_version_number} · {new Date(a.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <Pill tone={STATUS_TONES[a.status]} testId={`asset-status-${a.status}`}>{a.status}</Pill>
                    <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>{isOpen ? "▲" : "▼"}</span>
                  </div>
                </div>
              </button>

              {isOpen && (
                <div data-testid={`asset-versions-${a.id}`} style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--bo-line)" }}>
                  <div className="bo-eyebrow" style={{ marginBottom: 10 }}>Versions ({versions.length})</div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {versions.map((v) => (
                      <div
                        key={v.id}
                        style={{
                          padding: "10px 12px",
                          background: "var(--bo-panel-alt)",
                          border: "1px solid var(--bo-line)",
                          borderRadius: 8,
                        }}
                        data-testid={`version-row-${v.id}`}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                              v{v.version_number}
                              <span style={{ color: "var(--bo-muted)", fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                                {v.origin === "edit" ? "edited" : "generated"}
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)", marginTop: 2 }}>
                              {(v.output?.variants?.[0]?.headline || "(no headline)").slice(0, 80)}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", fontSize: 11 }}>
                            <Pill tone={GUARDIAN_TONES[v.guardian_status || "pending"]}>Guardian: {v.guardian_status || "pending"}</Pill>
                            {v.cost_usd != null && <Pill tone="muted">{formatDollars(v.cost_usd)}</Pill>}
                            {/* 2026-10-26 — "Open in Studio →" link
                                removed; the legacy Studio page is
                                deleted. Historical versions remain
                                readable in-list; retouching copy now
                                happens on the parent campaign's
                                Creative tab. */}
                          </div>
                        </div>
                        {v.parent_version_id && (
                          <div style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)", marginTop: 6 }}>
                            parent v: {v.parent_version_id.slice(0, 8)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Shell>
  );
}
